import { useMemo } from 'react';
import { Box, useTheme, Typography } from '@mui/material';
import { BaseAssetProps } from '../types';
import { AssetWrapper } from '../assetWrapper/AssetWrapper';
import { useAuthentication } from 'src/features/authentication/context';
import { useGetTagByIdQuery } from 'src/hooks/reactQueryApiHooks/useGetTagByIdQuery';
import { useWebSockets } from 'src/contexts/webSocketsContext/websockets.context';

type Props = BaseAssetProps & {
  isLevelShape?: boolean;
};

export const WaterLevelElement = ({ selected, width, height, data, isLevelShape, id }: Props) => {
  const theme = useTheme();
  const {
    customerId: { value: customerId },
  } = useAuthentication();

  const widthOfElement = width ?? 180;
  const heightOfElement = height ?? 380;

  const tankHeight = heightOfElement * 0.99;

  const tagId = data?.tagId;
  const gatewayId = data?.gatewayId;
  const siteId = data?.siteId;
  const wellHeight = data?.wellHeight;

  const { tag } = useGetTagByIdQuery({
    customerId: customerId!,
    siteId: siteId,
    inputId: tagId,
    gatewayId: gatewayId,
  });

  const { messages } = useWebSockets();
  const liveDataComplete = useMemo(() => {
    if (!messages || !gatewayId) return null;

    if (Array.isArray(messages)) {
      const matchedMessage = messages.find((msg: any) => msg?.data?.sourceId === gatewayId);
      if (!matchedMessage) return null;

      return { tags: matchedMessage.data.data };
    }

    if (messages?.data?.sourceId === gatewayId) {
      return { tags: messages.data.data };
    }

    return null;
  }, [messages, gatewayId]);

  const liveTag = liveDataComplete?.tags?.find((liveTag: any) => liveTag.id === tag?.id);
  const unit = liveTag ? liveTag?.rawUnit : tag?.rawUnit;

  const waterLevelValue = liveTag ? liveTag.value : tag?.value;

  const waterPercent = wellHeight && waterLevelValue != null ? waterLevelValue / wellHeight : 0;
  const waterHeightPx = Math.min(waterPercent * tankHeight, tankHeight);

  const labelBottom = Math.min(waterHeightPx, tankHeight - 58);

  return (
    <AssetWrapper
      selected={selected}
      width={widthOfElement}
      height={heightOfElement}
      id={id}
      key={`${data?.siteId}-${data?.assetId}`}
    >
      <Box
        data-sm="water-level-element"
        sx={{
          position: 'relative',
          width: widthOfElement,
          height: heightOfElement,
          border: isLevelShape ? 'none' : `0.1px solid ${theme.palette.grey[400]}`,
          background: '#f4f4f7',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: `${waterHeightPx}px`,
            backgroundColor: '#cbdef7',
            transition: 'height 0.3s ease',
          }}
        />

        {waterLevelValue !== null && (
          <Typography
            data-sm="water-level-value"
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: `${labelBottom}px`,
              left: '50%',
              transform: 'translateX(-50%)',
              fontWeight: 500,
              fontSize: `${Math.max(widthOfElement, heightOfElement) * 0.035}px`,
              px: 0.5,
              borderRadius: 1,
              textAlign: 'center',
            }}
          >
            {waterLevelValue} {unit}
          </Typography>
        )}
      </Box>
    </AssetWrapper>
  );
};
